import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GRADES, GRADE_HELP, GRADE_LABEL, RARITY_STYLES, colorHex, REGION_OPTIONS } from "@/lib/constants";
import { ConfirmButton } from "@/components/ConfirmButton";
import { Layers, DollarSign, Gem, Trash2, Palette, Save, Globe, FileSpreadsheet, Loader2, Lock, BookOpen, X, BookMarked, Camera } from "lucide-react";
import { toast } from "sonner";

export default function Collection() {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [colorFilter, setColorFilter] = useState(null);
  const isPublic = user?.collection_public !== false;

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/collection/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "drops-of-honey-collection.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel file downloaded");
    } catch (e) {
      toast.error("Could not download");
    } finally {
      setDownloading(false);
    }
  };

  const togglePrivacy = async () => {
    const next = !isPublic;
    try {
      await api.put("/auth/profile", { collection_public: next });
      await refreshUser();
      toast.success(next ? "Collection is now public" : "Collection is now private");
    } catch (e) {
      toast.error("Could not update visibility");
    }
  };

  const load = async () => {
    setLoading(true);
    const [i, s] = await Promise.all([api.get("/collection"), api.get("/collection/stats")]);
    setItems(i.data);
    setStats(s.data);
    setDirty({});
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const editItem = (id, changes) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
    setDirty((d) => ({ ...d, [id]: true }));
  };

  const saveItem = async (item) => {
    const payload = { insulator_id: item.insulator_id, grade: item.grade, quantity: item.quantity, variant_color: item.variant_color || "", features: item.features || "", embossing: item.embossing || "", purchase_price: item.purchase_price, storage_location: item.storage_location, condition_notes: item.condition_notes, us_state: item.us_state || "", photos: item.photos || [] };
    await api.put(`/collection/${item.id}`, payload);
    toast.success("Changes saved");
    await load();
  };

  const uploadMyPhoto = async (id, e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    try {
      const url = await uploadFile(file);
      const item = items.find((x) => x.id === id);
      const photos = [...(item?.photos || []), url];
      editItem(id, { photos });
      await api.put(`/collection/${id}`, { insulator_id: item.insulator_id, grade: item.grade, quantity: item.quantity, variant_color: item.variant_color || "", features: item.features || "", embossing: item.embossing || "", purchase_price: item.purchase_price, storage_location: item.storage_location, condition_notes: item.condition_notes, us_state: item.us_state || "", photos });
      toast.success("Your photo was added");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/collection/${id}`);
    toast.success("Removed from collection");
    load();
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-page animate-pulse font-tech">Loading your collection…</div>;

  const colorEntries = Object.entries(stats?.colors || {}).sort((a, b) => b[1] - a[1]);
  const maxColor = Math.max(1, ...colorEntries.map(([, c]) => c));
  const visibleItems = colorFilter ? items.filter((it) => (it.variant_color || it.insulator?.color) === colorFilter) : items;

  const controlsFor = (it) => {
    const ins = it.insulator || {};
    const shownColor = it.variant_color || ins.color;
    const canonCountry = ["United States", "USA", "US"].includes(ins.country) ? "United States of America" : ins.country;
    const regionOptions = REGION_OPTIONS[canonCountry] || [];
    const mine = it.photos && it.photos.length ? it.photos[0] : null;
    const src = mine || ins.image_url;
    return {
      ins, shownColor, regionOptions, mine, src,
      photoBtn: (
        <label className="cursor-pointer shrink-0 w-8 h-8 rounded-full grid place-items-center surface border text-muted-page hover:brand-text" title="Add your own photo of this piece" data-testid={`add-my-photo-${it.id}`}>
          <Camera className="w-4 h-4" />
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMyPhoto(it.id, e)} />
        </label>
      ),
      removeBtn: (
        <ConfirmButton testId={`remove-item-${it.id}`} onConfirm={() => remove(it.id)} title="Remove from collection?" description={`Remove ${ins.name} from your collection? This can't be undone.`} confirmLabel="Yes, remove" className="shrink-0 w-9 h-9 rounded-full grid place-items-center text-rose-500 border border-rose-200 hover:text-white hover:bg-rose-500 transition-colors">
          <Trash2 className="w-4 h-4" />
        </ConfirmButton>
      ),
      grade: (
        <select value={it.grade} onChange={(e) => editItem(it.id, { grade: e.target.value })} className="surface border rounded-lg px-2 py-1.5 text-sm" title={GRADE_HELP[it.grade]}>
          {GRADES.map((g) => <option key={g} value={g}>{GRADE_LABEL[g] || g}</option>)}
        </select>
      ),
      qty: (
        <select data-testid={`quantity-select-${it.id}`} value={it.quantity} onChange={(e) => editItem(it.id, { quantity: parseInt(e.target.value) || 1 })} className="w-16 surface border rounded-lg px-2 py-1.5">
          {Array.from(new Set([...Array.from({ length: 25 }, (_, i) => i + 1), it.quantity || 1])).sort((a, b) => a - b).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      ),
      color: (
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border border-black/10 shrink-0" style={{ background: colorHex(shownColor) }} />
          <input value={it.variant_color || ""} onChange={(e) => editItem(it.id, { variant_color: e.target.value })} className="w-24 surface border rounded-lg px-2 py-1.5" placeholder={ins.color} />
        </div>
      ),
      features: (
        <input value={it.features || ""} onChange={(e) => editItem(it.id, { features: e.target.value })} className="w-full sm:w-28 surface border rounded-lg px-2 py-1.5" placeholder="—" />
      ),
      emboss: (
        <input data-testid={`emboss-input-${it.id}`} value={it.embossing || ""} onChange={(e) => editItem(it.id, { embossing: e.target.value })} className="w-full sm:w-36 surface border rounded-lg px-2 py-1.5" placeholder={ins.embossing || "—"} />
      ),
      price: (
        <input type="number" min={0} step="0.01" value={it.purchase_price} onFocus={() => { if (Number(it.purchase_price) === 0) editItem(it.id, { purchase_price: "" }); }} onBlur={(e) => { if (e.target.value === "") editItem(it.id, { purchase_price: 0 }); }} onChange={(e) => editItem(it.id, { purchase_price: e.target.value === "" ? "" : parseFloat(e.target.value) || 0 })} className="w-24 surface border rounded-lg px-2 py-1.5" />
      ),
      location: (
        <input value={it.storage_location || ""} onChange={(e) => editItem(it.id, { storage_location: e.target.value })} className="w-full sm:w-32 surface border rounded-lg px-2 py-1.5" placeholder="—" />
      ),
      region: (
        <select data-testid={`us-state-select-${it.id}`} value={it.us_state || ""} onChange={(e) => editItem(it.id, { us_state: e.target.value })} className="w-full sm:w-32 surface border rounded-lg px-2 py-1.5 text-sm" disabled={!regionOptions.length}>
          <option value="">—</option>
          {regionOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ),
      save: dirty[it.id] ? (
        <ConfirmButton testId={`save-item-${it.id}`} onConfirm={() => saveItem(it)} title="Save changes?" description={`Save your changes to ${ins.name}?`} confirmLabel="Yes, save" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-white" style={{ background: "var(--brand)" }}>
          <Save className="w-3 h-3" /> Save
        </ConfirmButton>
      ) : null,
    };
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
      <p className="font-tech text-xs uppercase tracking-widest brand-text">Your vault</p>
      <h1 className="font-display text-4xl font-bold mb-4">My Collection</h1>

      <div className="flex flex-wrap items-center gap-2 mb-8">
        <span data-testid="tab-collection-active" className="px-4 py-2 rounded-full text-sm font-semibold text-white inline-flex items-center gap-2" style={{ background: "var(--brand)" }}><BookOpen className="w-4 h-4" /> My Collection</span>
        <Link to="/collection/map" data-testid="tab-map-link" className="px-4 py-2 rounded-full text-sm font-semibold surface border inline-flex items-center gap-2"><Globe className="w-4 h-4" /> World Map</Link>
        <Link to="/collection/passport" data-testid="tab-passport-link" className="px-4 py-2 rounded-full text-sm font-semibold surface border inline-flex items-center gap-2"><BookMarked className="w-4 h-4" /> Passport</Link>
        <button data-testid="download-excel-btn" onClick={downloadExcel} disabled={downloading} className="px-4 py-2 rounded-full text-sm font-semibold surface border inline-flex items-center gap-2 disabled:opacity-60">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Download Excel
        </button>
        <button data-testid="collection-privacy-toggle" onClick={togglePrivacy} className="px-4 py-2 rounded-full text-sm font-semibold surface border inline-flex items-center gap-2 ml-auto">
          {isPublic ? <><Globe className="w-4 h-4 brand-text" /> Public</> : <><Lock className="w-4 h-4 text-muted-page" /> Private</>}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8 stagger">
        {[
          { icon: Layers, label: "Total Insulators", value: stats?.total_count ?? 0 },
          { icon: Gem, label: "Unique Pieces", value: stats?.unique_count ?? 0 },
          { icon: DollarSign, label: "Est. Value", value: `$${(stats?.total_value ?? 0).toLocaleString()}` },
          { icon: Gem, label: "Rare / Ultra Rare", value: stats?.rare_count ?? 0 },
        ].map((s) => (
          <div key={s.label} className="surface border rounded-2xl p-5 glass-glow card-lift" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="flex items-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><s.icon className="w-4 h-4" /> {s.label}</div>
            <p className="font-display text-3xl font-bold mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {colorEntries.length > 0 && (
        <div className="surface border rounded-2xl p-5 mb-8">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><Palette className="w-4 h-4" /> Color Spectrum</div>
            {colorFilter && (
              <button data-testid="clear-color-filter" onClick={() => setColorFilter(null)} className="text-xs brand-text flex items-center gap-1 font-semibold">
                <X className="w-3 h-3" /> Showing {colorFilter} · clear
              </button>
            )}
          </div>
          <div className="space-y-2">
            {colorEntries.map(([c, n]) => {
              const active = colorFilter === c;
              return (
                <button
                  key={c}
                  data-testid={`color-spectrum-bar-${c.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  onClick={() => setColorFilter(active ? null : c)}
                  className={`w-full flex items-center gap-3 rounded-lg px-1.5 py-1 transition-colors hover:bg-black/[0.04] ${active ? "ring-2 ring-amber-400" : ""}`}
                  title={active ? "Clear filter" : `Show only ${c}`}
                >
                  <span className="w-24 text-sm shrink-0 text-left flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full border border-black/10 shrink-0" style={{ background: colorHex(c) }} />
                    <span className="truncate">{c}</span>
                  </span>
                  <div className="flex-1 h-4 rounded-full bg-black/5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(n / maxColor) * 100}%`, background: colorHex(c) }} />
                  </div>
                  <span className="font-tech text-sm w-8 text-right">{n}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-20 surface border rounded-2xl">
          <p className="font-display text-2xl">Your collection is empty.</p>
          <p className="text-muted-page mt-1">Browse the catalog and add your first drop of honey.</p>
          <Link to="/catalog" className="inline-block mt-4 px-5 py-2.5 rounded-full font-semibold" style={{ background: "var(--brand)", color: "#fff" }}>Browse catalog</Link>
        </div>
      ) : (
        <>
          {/* Desktop / tablet: table */}
          <div className="hidden lg:block surface border rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid="collection-table">
              <thead>
                <tr className="text-left font-tech text-xs uppercase tracking-widest text-muted-page border-b" style={{ borderColor: "var(--page-border)" }}>
                  <th className="px-4 py-3">Insulator</th>
                  <th className="px-4 py-3">Grade</th>
                  <th className="px-4 py-3">Qty</th>
                  <th className="px-4 py-3">Color</th>
                  <th className="px-4 py-3">Features</th>
                  <th className="px-4 py-3">Embossing</th>
                  <th className="px-4 py-3">Paid ($)</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Region</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((it) => {
                  const c = controlsFor(it);
                  return (
                    <tr key={it.id} className="border-b hover:bg-black/[0.02]" style={{ borderColor: "var(--page-border)" }} data-testid={`collection-row-${it.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Link to={`/catalog/${it.insulator_id}`} className="flex items-center gap-3">
                            {c.src ? <img src={mediaUrl(c.src)} alt="" title={c.mine ? "Your photo" : "Catalog photo"} className="w-12 h-12 object-contain rounded-lg" style={{ background: `${colorHex(c.shownColor)}18`, boxShadow: c.mine ? "0 0 0 2px var(--honey-500)" : "none" }} /> : null}
                            <div>
                              <div className="font-display text-lg font-semibold leading-tight">{c.ins.name}</div>
                              <div className="text-xs text-muted-page font-tech">{c.ins.cd_number}{it.photos?.length ? ` · ${it.photos.length} of your photos` : ""}</div>
                            </div>
                          </Link>
                          {c.photoBtn}
                        </div>
                      </td>
                      <td className="px-4 py-3">{c.grade}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">{c.qty}{c.removeBtn}</div>
                      </td>
                      <td className="px-4 py-3">{c.color}</td>
                      <td className="px-4 py-3">{c.features}</td>
                      <td className="px-4 py-3">{c.emboss}</td>
                      <td className="px-4 py-3">{c.price}</td>
                      <td className="px-4 py-3">{c.location}</td>
                      <td className="px-4 py-3">{c.region}</td>
                      <td className="px-4 py-3">{c.save}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <div className="lg:hidden space-y-4" data-testid="collection-cards">
            {visibleItems.map((it) => {
              const c = controlsFor(it);
              return (
                <div key={it.id} className="surface border rounded-2xl p-4" data-testid={`collection-card-${it.id}`}>
                  <div className="flex items-start gap-3">
                    <Link to={`/catalog/${it.insulator_id}`} className="shrink-0">
                      {c.src ? <img src={mediaUrl(c.src)} alt="" title={c.mine ? "Your photo" : "Catalog photo"} className="w-14 h-14 object-contain rounded-lg" style={{ background: `${colorHex(c.shownColor)}18`, boxShadow: c.mine ? "0 0 0 2px var(--honey-500)" : "none" }} /> : null}
                    </Link>
                    <Link to={`/catalog/${it.insulator_id}`} className="flex-1 min-w-0">
                      <div className="font-display text-lg font-semibold leading-tight">{c.ins.name}</div>
                      <div className="text-xs text-muted-page font-tech">{c.ins.cd_number}{it.photos?.length ? ` · ${it.photos.length} of your photos` : ""}</div>
                    </Link>
                    {c.removeBtn}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Grade</span>
                      {c.grade}
                    </label>
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Qty</span>
                      {c.qty}
                    </label>
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Color</span>
                      {c.color}
                    </label>
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Paid ($)</span>
                      {c.price}
                    </label>
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Features</span>
                      {c.features}
                    </label>
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Embossing</span>
                      {c.emboss}
                    </label>
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Location</span>
                      {c.location}
                    </label>
                    <label className="text-xs">
                      <span className="block font-tech uppercase tracking-widest text-muted-page mb-1">Region</span>
                      {c.region}
                    </label>
                  </div>

                  <div className="flex items-center gap-3 mt-4">
                    {c.photoBtn}
                    <span className="text-xs text-muted-page">Add your photo</span>
                    <div className="ml-auto">{c.save}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
